/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { Unteraufgabe } from '../models/Unteraufgabe';
import type { UnteraufgabeDaten } from '../models/UnteraufgabeDaten';
import type { UnteraufgabenListe } from '../models/UnteraufgabenListe';

import type { CancelablePromise } from '../core/CancelablePromise';
import type { BaseHttpRequest } from '../core/BaseHttpRequest';

export class UnteraufgabenService {

  constructor(public readonly httpRequest: BaseHttpRequest) {}

  /**
   * Eine Liste aller Unteraufgaben
   * Gibt eine Liste aller Unteraufgaben zu einer Aufgabe zurück
   * @returns UnteraufgabenListe Liste der gesuchten Unteraufgaben zu einer Aufgabe
   * @throws ApiError
   */
  public searchUnteraufgaben({
    aufgabeId,
    search,
    filtered,
    page,
    limit,
    sort,
  }: {
    /**
     * Eindeutige ID der Aufgabe für (searchUnteraufgaben)
     */
    aufgabeId: string,
    /**
     * Suchstring für Unteraufgaben
     */
    search?: string,
    /**
     * Filterstring für Unteraufgaben Seite
     */
    filtered?: string,
    /**
     * Seitenzahl der Unteraufgaben Seite
     */
    page?: number,
    /**
     * Limit der Liste von Unteraufgaben Einträgen
     */
    limit?: number,
    /**
     * Sortierung der Unteraufgaben Seite
     */
    sort?: string,
  }): CancelablePromise<UnteraufgabenListe> {
    return this.httpRequest.request({
      method: 'GET',
      url: '/api/v1/aufgaben/{aufgabeId}/unteraufgaben',
      path: {
        'aufgabeId': aufgabeId,
      },
      query: {
        'search': search,
        'filtered': filtered,
        'page': page,
        'limit': limit,
        'sort': sort,
      },
      errors: {
        400: `Bad Request for this request (searchUnteraufgaben)`,
        401: `Unauthorized for this request (searchUnteraufgaben)`,
        403: `Forbidden for this request (searchUnteraufgaben)`,
        404: `Not Found for this request (searchUnteraufgaben)`,
      },
    });
  }

  /**
   * Eine Unteraufgabe erstellen
   * Erstellt eine Unteraufgabe
   * @returns Unteraufgabe Die erstellte Unteraufgabe
   * @throws ApiError
   */
  public createUnteraufgabe({
    aufgabeId,
    requestBody,
  }: {
    /**
     * Eindeutige ID der Aufgabe für (createUnteraufgabe)
     */
    aufgabeId: string,
    /**
     * Daten, mit welchen die Unteraufgabe erstellt werden soll
     */
    requestBody: UnteraufgabeDaten,
  }): CancelablePromise<Unteraufgabe> {
    return this.httpRequest.request({
      method: 'POST',
      url: '/api/v1/aufgaben/{aufgabeId}/unteraufgaben',
      path: {
        'aufgabeId': aufgabeId,
      },
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        401: `Unauthorized for this request (createUnteraufgabe)`,
        403: `Forbidden for this request (createUnteraufgabe)`,
      },
    });
  }

  /**
   * Eine Unteraufgabe bekommen
   * Bekomme eine Unteraufgabe
   * @returns Unteraufgabe Die Unteraufgabe
   * @throws ApiError
   */
  public getUnteraufgabe({
    aufgabeId,
    unteraufgabeId,
  }: {
    /**
     * Eindeutige ID der Aufgabe für (getUnteraufgabe)
     */
    aufgabeId: string,
    /**
     * Eindeutige ID der Unteraufgabe für (getUnteraufgabe)
     */
    unteraufgabeId: string,
  }): CancelablePromise<Unteraufgabe> {
    return this.httpRequest.request({
      method: 'GET',
      url: '/api/v1/aufgaben/{aufgabeId}/unteraufgaben/{unteraufgabeId}',
      path: {
        'aufgabeId': aufgabeId,
        'unteraufgabeId': unteraufgabeId,
      },
      errors: {
        401: `Unauthorized for this request (getUnteraufgabe)`,
        403: `Forbidden for this request (getUnteraufgabe)`,
        404: `Not Found for this request (getUnteraufgabe)`,
      },
    });
  }

  /**
   * Eine Unteraufgabe aktualisieren
   * Aktualisiert eine Unteraufgabe
   * @returns Unteraufgabe Die aktualisierte Unteraufgabe
   * @throws ApiError
   */
  public updateUnteraufgabe({
    aufgabeId,
    unteraufgabeId,
    requestBody,
  }: {
    /**
     * Eindeutige ID der Aufgabe für (updateUnteraufgabe)
     */
    aufgabeId: string,
    /**
     * Eindeutige ID der Unteraufgabe für (updateUnteraufgabe)
     */
    unteraufgabeId: string,
    /**
     * Daten, mit welchen die Unteraufgabe aktualisiert werden soll
     */
    requestBody: UnteraufgabeDaten,
  }): CancelablePromise<Unteraufgabe> {
    return this.httpRequest.request({
      method: 'PUT',
      url: '/api/v1/aufgaben/{aufgabeId}/unteraufgaben/{unteraufgabeId}',
      path: {
        'aufgabeId': aufgabeId,
        'unteraufgabeId': unteraufgabeId,
      },
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        401: `Unauthorized for this request (updateUnteraufgabe)`,
        403: `Forbidden for this request (updateUnteraufgabe)`,
        404: `Not Found for this request (updateUnteraufgabe)`,
      },
    });
  }

  /**
   * Eine Unteraufgabe löschen
   * Löscht eine Unteraufgabe
   * @returns Unteraufgabe Die gelöschte Unteraufgabe
   * @throws ApiError
   */
  public deleteUnteraufgabe({
    aufgabeId,
    unteraufgabeId,
  }: {
    /**
     * Eindeutige ID der Aufgabe für (deleteUnteraufgabe)
     */
    aufgabeId: string,
    /**
     * Eindeutige ID der Unteraufgabe für (deleteUnteraufgabe)
     */
    unteraufgabeId: string,
  }): CancelablePromise<Unteraufgabe> {
    return this.httpRequest.request({
      method: 'DELETE',
      url: '/api/v1/aufgaben/{aufgabeId}/unteraufgaben/{unteraufgabeId}',
      path: {
        'aufgabeId': aufgabeId,
        'unteraufgabeId': unteraufgabeId,
      },
      errors: {
        401: `Unauthorized for this request (deleteUnteraufgabe)`,
        403: `Forbidden for this request (deleteUnteraufgabe)`,
        404: `Not Found for this request (deleteUnteraufgabe)`,
      },
    });
  }

}
